import { ChangeDetectorRef, Component } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Booking, BookingType } from 'src/app/shared/models/booking/booking';
import { Observable, takeUntil, Subject, tap, map, switchMap, of, combineLatest, startWith, distinctUntilChanged } from 'rxjs';
import { UsersService } from 'src/app/shared/api-services/users.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BookingService } from 'src/app/shared/api-services/booking.service';
import { BookingRequest } from 'src/app/shared/models/booking/booking-request';
import { TypeExtraField } from 'src/app/shared/models/common';
import { ExtraFieldsUtilsService } from 'src/app/shared/utilities/extra-fields-utils.servic';
import { DateUtilsService } from 'src/app/shared/utilities/date-utils.service';
import { BookingModalService } from '../booking-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MeService } from 'src/app/shared/api-services/me.service';
import { animate, style, trigger } from '@angular/animations';
import { state } from '@angular/animations';
import { transition } from '@angular/animations';
import { UserModel } from 'src/app/shared/models/user/users';
import { Headquarter } from 'src/app/shared/models/headquarter/headquarter';
import { LocationModel } from 'src/app/shared/models/location/location';
import * as Permissions from 'src/app/shared/constant/permissions';
import { PermissionModalUtilsService } from '../../permission-modal/permission-modal-utils.service';
import { PermissionResponse } from 'src/app/shared/models/permission/permission-response';

@Component({
    selector: 'booking-modal-step1',
    templateUrl: './booking-modal-step1.component.html',
    styleUrl: './booking-modal-step1.component.scss',
    animations: [
      trigger('gotop', [
        state('top', style({ transform: 'translateY(0)' })),
        state('bottom', style({ transform: 'translateY(100%)' })),
        transition('top <=> bottom', animate('00ms ease-in-out'))
      ])
    ],
    standalone: false
})
export class BookingModalStep1Component {

  booking: Booking;
  get canBookForOthers() {
    return this._bookingModalNewService.canDoBookingForOtherUsers;
  }
  bookingType: BookingType;
  endDateMaxDate: Date | null = null;
  startDateMaxDate: Date | null = null;

  form: FormGroup;
  fields: FormlyFieldConfig[];
  typeExtraFields: TypeExtraField[];

  currentUser: UserModel | null = null;
  driverNames$: Observable<string[]>;

  animationState = 'top';

  bookingTypeIds: string[] | null = null;
  headquarterIds: string[] | null = null;
  locationIds: string[] | null = null;

  firstTime = true;
  private _destroy = new Subject<void>();
  constructor(
    private _meService: MeService,
    private _userService: UsersService,
    private _bookingService: BookingService,
    private _extraFieldsUtilsService: ExtraFieldsUtilsService,
    private _dateUtilsService: DateUtilsService,
    private _bookingModalNewService: BookingModalService,
    private _translateService: TranslateService,
    private _toasterService: ToastrService,
    private _permissionModalUtilsService: PermissionModalUtilsService,
    private _cdr: ChangeDetectorRef,
  ) { 
    this.form = new FormGroup({
      titleBooking: new FormControl(null),
      headquarterId: new FormControl(null),
      locationId: new FormControl(null),
      bookingTypeId: new FormControl(null),
      userId: new FormControl(null),
      startDateBooking: new FormControl(null),
      startTimeBooking: new FormControl(null),
      endDateBooking: new FormControl(null),
      endTimeBooking: new FormControl(null),
      filterUser: new FormControl(false)
    });
  }

  ngOnInit(): void {
    this._bookingModalNewService.setStep1Component(this);
    this.setupDateValidation();
    this.getUserPermitted();
  }

  private setForm() {
    this.booking = this._bookingModalNewService.booking;
    let formValue = this._bookingModalNewService.getInformationStep(1);
    if (formValue) {
      this.form.patchValue(formValue);
      if (this.booking?.user) {
        this.currentUser = this.booking.user;
      }
      else if (this.form.get('userId')?.value) {
        this.getCurrentUser(this.form.get('userId')?.value).pipe(takeUntil(this._destroy)).subscribe();
      }
    }
    else if (this.booking?.id) {
      let startDate = new Date(this.booking.startDate);
      let startTime = this._dateUtilsService.parseDateToTimeString(new Date(this.booking.startDate));
      let endDate = new Date(this.booking.endDate);
      let endTime = this._dateUtilsService.parseDateToTimeString(new Date(this.booking.endDate));

      this.form.patchValue({
        titleBooking: this.booking.title,
        headquarterId: this.booking.headquarterId,
        locationId: this.booking.locationId,
        bookingTypeId: this.booking.bookingTypeId,
        userId: this.booking.userId,
        startDateBooking: startDate,
        startTimeBooking: startTime,
        endDateBooking: endDate,
        endTimeBooking: endTime
      });
      this.currentUser = this.booking.user;
    }
    else {
      this.getCurrentUser().pipe(takeUntil(this._destroy)).subscribe();
      this.setStartDateNow();
    }
  }

  private getCurrentUser(userId: string | null = null): Observable<UserModel> {
    if (userId) {
      return this._userService.getUserById$(userId).pipe(
        tap(response => {
          this.currentUser = response;
        })
      );
    } else {
      return this._meService.getMe$().pipe(
        tap(response => {
          this.currentUser = response;
          this.form.patchValue({
            userId: response.id,
          });
        })
      );
    }
  }

  private getUserPermitted(): void {
    const processPermissions = (response: PermissionResponse) => {
      const expressionManageBookings: string[] = response.items
        .filter(permission => permission.name === Permissions.CAN_MANAGE_BOOKINGS)
        .map(permission => permission.expr);
      this.processPermissionExpressions(expressionManageBookings);
    };

    if (!this.canBookForOthers) {
      this._meService.getMePermissions$()
        .pipe(
          takeUntil(this._destroy),
          map(processPermissions)
        ).subscribe(() => {
          if (this.firstTime) {
            this.firstTime = false;
            this.setForm();
          }
        });
    } else {
      // Combina userId e filterUser per reagire ai cambiamenti di entrambi
      combineLatest([
        this.form.get('userId')?.valueChanges.pipe(startWith(this.form.get('userId')?.value)) || of(null),
        this.form.get('filterUser')?.valueChanges.pipe(startWith(this.form.get('filterUser')?.value)) || of(true)
      ]).pipe(
        takeUntil(this._destroy),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        switchMap(([userId, filterUser]) => {
          if (!filterUser || !userId) {
            // Se filterUser è false, usa i permessi dell'utente corrente
            return this._meService.getMePermissions$();
          } else if (userId) {
            // Se filterUser è true e c'è un userId, usa i permessi dell'utente selezionato
            return this._userService.listUserPermissions$(userId);
          } else {
            // Se filterUser è true ma non c'è userId, resetta i permessi
            return of({ items: [] } as PermissionResponse);
          }
        }),
        map(processPermissions)
      ).subscribe(() => {
        if (this.firstTime) {
          this.firstTime = false;
          this.setForm();
        }
      });
    }
  }

  private processPermissionExpressions(
    expressionManageBookings: string[]
  ): void {
    var countBookingType = 0;
    expressionManageBookings.forEach(expr => {
      if (expr?.includes('BookingTypeId')) {
        countBookingType++;
      }
    });
    this.bookingTypeIds = this.processIdsByType(expressionManageBookings, 'BookingTypeId');
    // * mostro tutti i bookingType se manca almeno una filterExpression
    if (countBookingType !== expressionManageBookings.length) { 
      this.bookingTypeIds = null;
    }

    var countHeadquarter = 0;
    expressionManageBookings.forEach(expr => {
      if (expr?.includes('HeadquarterId')) {
        countHeadquarter++;
      }
    });
    const headquarterIds = this.processIdsByType(expressionManageBookings, 'HeadquarterId');
    this.manageHeadquarterExpression(headquarterIds);
    // * mostro tutte le headquarter se manca almeno una filterExpression
    if (countHeadquarter !== expressionManageBookings.length) { 
      this.headquarterIds = null;
    }

    var countLocation = 0;
    expressionManageBookings.forEach(expr => {
      if (expr?.includes('LocationId')) {
        countLocation++;
      }
    });
    this.locationIds = this.processIdsByType(expressionManageBookings, 'LocationId');
    // * mostro tutte le location se manca almeno una filterExpression
    if (countLocation !== expressionManageBookings.length) { 
      this.locationIds = null;
    }
  }

  private manageHeadquarterExpression(headquarterIds: string[]) {
    if (headquarterIds.length > 0 && headquarterIds.includes("#MEHEADQUARTERID#")) {
      // remove tag #MEHEADQUARTERID#
      headquarterIds.splice(headquarterIds.indexOf("#MEHEADQUARTERID#"), 1);
      this._userService.getUserById$(this.currentUser?.id).pipe(
        takeUntil(this._destroy),
        map(response => response.headquarterId)
      ).subscribe(headquarterId => {
        this.headquarterIds = [...headquarterIds, headquarterId];
      });
    }
  }

  private processIdsByType(
    expressionManage: string[],
    idType: string,
  ): string[] {
    let idsManage: string[] = [];
    if (expressionManage) {
      expressionManage.forEach(expr => {
        idsManage = [...idsManage, ...this._permissionModalUtilsService.readIdsFromExpression(expr, idType)];
      });
    }
    return [...new Set([...idsManage])];
  }

  private getExtraFields() {
    this._bookingService.getBookingFields$({ includeBookingTypeId: this.form.get('bookingTypeId')?.value })
    .pipe(takeUntil(this._destroy))
    .subscribe({
      next: (response) => {
        this.typeExtraFields?.forEach(field => {
          this.form.removeControl(field.name);
        });

        this.typeExtraFields = response.items.map(x => x as TypeExtraField);

        this.booking = this.booking ? this.booking : {};
        this.booking.extraFields = this.booking.extraFields ? this.booking.extraFields : {};

        this.booking.extraFields = this._extraFieldsUtilsService.parseDate(this.typeExtraFields, this.booking.extraFields, false);

        this.fields = this.typeExtraFields.map(field =>
        this._extraFieldsUtilsService.createFieldConfig(field, this.booking.extraFields));
      }
    });
  }

  private setStartDateNow() {
    this.updateTime();
  }

  private updateTime() {
    let now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    
    // Formatta l'ora in formato HH:MM
    let startTime = now.toTimeString().split(' ')[0].substring(0,5);
    
    this.form.patchValue({
      startDateBooking: now,
      startTimeBooking: startTime,
      endDateBooking: now,
    });

    this._cdr.detectChanges();
  }

  updateBookingRequest() {
    let startDate: string | null = null;
    let endDate: string | null = null;

    if (this.form.get('startDateBooking')?.value && this.form.get('startTimeBooking')?.value) {
      startDate = this._dateUtilsService
        .combineDateAndTimeToIsoString(
          this.form.get('startDateBooking')?.value,
          this.form.get('startTimeBooking')?.value
        );
    }

    if (this.form.get('endDateBooking')?.value && this.form.get('endTimeBooking')?.value) {
      endDate = this._dateUtilsService
        .combineDateAndTimeToIsoString(
          this.form.get('endDateBooking')?.value,
          this.form.get('endTimeBooking')?.value
        );
    }

    let bookingRequest: BookingRequest = {
      title: this.form.get('titleBooking')?.value,
      locationId: this.form.get('locationId')?.value,
      bookingTypeId: this.form.get('bookingTypeId')?.value,
      userId: this.form.get('userId')?.value,
      startDate: startDate,
      endDate: endDate,
      vehicleId: this.booking?.vehicleId ?? null
    }

    bookingRequest = this._extraFieldsUtilsService
      .updateRequestFromForm(this.typeExtraFields, this.form.value, bookingRequest);
    if (bookingRequest.extraFields) {
      bookingRequest.extraFields = this._extraFieldsUtilsService
        .parseBool(this.typeExtraFields, bookingRequest.extraFields);
    }

    this._bookingModalNewService.setBookingRequest(bookingRequest);
  }

  checkFormValidity() : boolean {
    if (!this.form.get('locationId')?.value) {
      this.form.get('locationId')?.setErrors({ invalid: true });
      this._toasterService.error(this._translateService.instant('BOOKINGS.MESSAGES.ERROR_LOCATION'));
      return false;
    }
    if (!this.form.get('bookingTypeId')?.value) {
      this.form.get('bookingTypeId')?.setErrors({ invalid: true });
      this._toasterService.error(this._translateService.instant('BOOKINGS.MESSAGES.ERROR_BOOKING_TYPE'));
      return false;
    }
    if (!this.form.get('userId')?.value) {
      this.form.get('userId')?.setErrors({ invalid: true });
      this._toasterService.error(this._translateService.instant('BOOKINGS.MESSAGES.ERROR_DRIVER_REQUIRED'));
      return false;
    }
    if (!this.form.get('startDateBooking')?.value || !this.form.get('startTimeBooking')?.value) {
      this.form.get('startDateBooking')?.setErrors({ invalid: true });
      this._toasterService.error(this._translateService.instant('BOOKINGS.MESSAGES.ERROR_REQUIRED_START_DATE'));
      return false;
    }
    if (!this.form.get('endDateBooking')?.value || !this.form.get('endTimeBooking')?.value) {
      this.form.get('endDateBooking')?.setErrors({ invalid: true });
      this._toasterService.error(this._translateService.instant('BOOKINGS.MESSAGES.ERROR_REQUIRED_END_DATE'));
      return false;
    }

    let startDate: Date | null = null;
    let endDate: Date | null = null;

    if (this.form.get('startDateBooking')?.value && this.form.get('startTimeBooking')?.value) {
      startDate = new Date(this._dateUtilsService
        .combineDateAndTimeToIsoString(
          this.form.get('startDateBooking')?.value,
          this.form.get('startTimeBooking')?.value
        )
      );
    }

    if (this.form.get('endDateBooking')?.value && this.form.get('endTimeBooking')?.value) {
      endDate = new Date(this._dateUtilsService
        .combineDateAndTimeToIsoString(
          this.form.get('endDateBooking')?.value,
          this.form.get('endTimeBooking')?.value
        )
      );
    }

    if (startDate && endDate && startDate > endDate) {
      this.form.get('endDateBooking')?.setErrors({ invalid: true });
      this._toasterService.error(this._translateService.instant('BOOKINGS.MESSAGES.ERROR_STARTDATE_BOOKING'));
      return false;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return this.focusOnInvalidField();
    }

    this._bookingModalNewService.saveInformationStep(1, this.form.value);
    return true;
  }

  focusOnInvalidField() : boolean {
    const invalidField = this.fields.find(field => field.formControl && field.formControl.invalid && field.formControl.touched);
    if (invalidField) {
      const element = document.querySelector(`[id="${invalidField.id}"]`) as HTMLElement;
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }
    else {
      return true;
    }
  }

  onSelectedBookingType(bookingType: BookingType) {
    this.form.get('bookingTypeId')?.setValue(bookingType.id);
    this.bookingType = bookingType;
    this._bookingModalNewService.setBookingType(this.bookingType);
    this._bookingModalNewService.hideStepMap();
    this.getExtraFields();
    
    // Calcolo della data massima per startDate e endDate se maxEndDateMinutes è definito
    if (this.bookingType?.maxEndDateMinutes) {
      this.updateStartDateMaxDate();
      this.updateEndDateMaxDate();
      
      // Ascolto i cambiamenti per aggiornare le date massime
      this.form.get('startDateBooking')?.valueChanges
        .pipe(takeUntil(this._destroy))
        .subscribe(() => {
          this.updateStartDateMaxDate();
          this.updateEndDateMaxDate();
        });
    } else {
      this.startDateMaxDate = null;
      this.endDateMaxDate = null;
    }
  }

  private updateStartDateMaxDate() {
    if (!this.bookingType?.maxEndDateMinutes) {
      this.startDateMaxDate = null;
      return;
    }
    
    const now = new Date();
    this.startDateMaxDate = new Date(now);
    this.startDateMaxDate.setMinutes(now.getMinutes() + this.bookingType.maxEndDateMinutes);
  }

  private updateEndDateMaxDate() {
    if (!this.form.get('startDateBooking')?.value || !this.bookingType?.maxEndDateMinutes) {
      this.endDateMaxDate = null;
      return;
    }
    
    const startDate = new Date(this.form.get('startDateBooking')?.value);
    this.endDateMaxDate = new Date(startDate);
    this.endDateMaxDate.setMinutes(startDate.getMinutes() + this.bookingType.maxEndDateMinutes);
  }

  private setupDateValidation() {
    // Verifica la data di inizio quando cambia
    this.form.get('startDateBooking')?.valueChanges
      .pipe(takeUntil(this._destroy))
      .subscribe((value) => {
        if (value && this.startDateMaxDate) {
          const startDate = new Date(value);
          if (startDate > this.startDateMaxDate) {
            this.form.get('startDateBooking')?.setErrors({ invalid: true });
            let date = this.startDateMaxDate.toLocaleString('it-IT', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
            this._toasterService.error(this._translateService.instant('BOOKINGS.MESSAGES.ERROR_MAX_START_DATE', { maxDate: date }));
          }
          else {
            this.form.get('startDateBooking')?.setErrors(null);
          }
        }
      });

    // Verifica la data di fine quando cambia
    this.form.get('endDateBooking')?.valueChanges
      .pipe(takeUntil(this._destroy))
      .subscribe((value) => {
        if (value && this.endDateMaxDate) {
          const endDate = new Date(value);
          if (endDate > this.endDateMaxDate) {
            this.form.get('endDateBooking')?.setErrors({ invalid: true });
            let date = this.endDateMaxDate.toLocaleString('it-IT', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
            this._toasterService.error(this._translateService.instant('BOOKINGS.MESSAGES.ERROR_MAX_END_DATE', { maxDate: date }));
          }
          else {
            this.form.get('endDateBooking')?.setErrors(null);
          }
        }
      });
  }

  onDriverSelected(user: UserModel) {
    this.form.patchValue({
      userId: user?.id ?? null,
    });
  }

  onHeadquarterSelected(headquarter: Headquarter) {
    this.form.patchValue({
      headquarterId: headquarter?.id ?? null,
    });
  }

  onLocationSelected(location: LocationModel) {
    this.form.patchValue({
      locationId: location?.id ?? null
    });
  }

  ngOnDestroy(): void {
    this.animationState = 'top';
    this._destroy.next();
    this._destroy.complete();
  }
}
